import React, { useEffect, useRef, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { AxiosError } from 'axios';
import { Loader2, Upload, ImageIcon, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { useNotifications } from '@/hooks/useNotifications';
import { useUpload } from '@/hooks/useUpload';
import { uploadFile } from '@/api/upload';
import {
  useCreateMerchant,
  useCreateMerchantCategory,
  useMerchantCategories,
  useUpdateMerchant,
} from '@/hooks/useVendors';
import { useStates, useCities } from '@/hooks/useLocation';
import { Merchant } from '@/api/vendors';
import { SearchableSelect } from '@/components/common/SearchableSelect';
import { cn } from '@/utils';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { MultiSelect } from '@/components/common/MultiSelect';

const ADD_CATEGORY_VALUE = '__add_category__';

const vendorSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  description: z.string().min(1, 'Description is required'),
  industryCategoryIds: z.array(z.string()).min(1, 'Select at least one category'),
  city: z.string().min(1, 'City is required'),
  state: z.string().min(1, 'State is required'),
  address: z.string().optional(),
  mapUrl: z.string().optional(),
  earnRate: z.coerce.number().min(0, 'Must be 0 or more'),
  isFeatured: z.boolean(),
  logo: z.string().min(1, 'Logo is required'),
  banner: z.string().min(1, 'Banner is required'),
  images: z.array(z.string()).default([]),
  websiteUrl: z.string().optional(),
  contactPerson: z.string().min(1, 'Contact person is required'),
  contactMobile: z.string().regex(/^\d{10}$/, 'Enter a valid 10-digit mobile number'),
  businessEmail: z.string().email('Enter a valid email').optional().or(z.literal('')),
});

type VendorFormData = z.infer<typeof vendorSchema>;

const emptyDefaults: VendorFormData = {
  name: '',
  description: '',
  industryCategoryIds: [],
  city: '',
  state: '',
  address: '',
  mapUrl: '',
  earnRate: 0,
  isFeatured: false,
  logo: '',
  banner: '',
  images: [],
  websiteUrl: '',
  contactPerson: '',
  contactMobile: '',
  businessEmail: '',
};

function merchantToFormData(vendor: Merchant) {
  let categoryIds: string[] = [];
  if (vendor.industryCategories && vendor.industryCategories.length > 0) {
    categoryIds = vendor.industryCategories.map((c) => String(c.id));
  } else if (vendor.industryCategory) {
    categoryIds = [String(vendor.industryCategory.id)];
  }

  return {
    name: vendor.name,
    description: vendor.description,
    industryCategoryIds: categoryIds,
    city: vendor.city,
    state: vendor.state ?? '',
    address: vendor.address ?? '',
    mapUrl: vendor.mapUrl ?? '',
    earnRate: vendor.earnRate,
    isFeatured: vendor.isFeatured,
    logo: vendor.logo,
    banner: vendor.banner,
    images: vendor.images ?? [],
    websiteUrl: vendor.websiteUrl ?? '',
    contactPerson: vendor.contactPerson,
    contactMobile: vendor.contactMobile.replace(/^\+91/, ''),
    businessEmail: vendor.businessEmail ?? '',
  };
}

interface VendorFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  vendor?: Merchant;
}

interface ImageUploadFieldProps {
  label: string;
  preview: string | null;
  isUploading: boolean;
  error?: string;
  resolutionInfo?: string;
  sizeInfo?: string;
  onFileSelect: (file: File) => void;
}

function ImageUploadField({
  label,
  preview,
  isUploading,
  error,
  resolutionInfo,
  sizeInfo,
  onFileSelect,
}: ImageUploadFieldProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  return (
    <div className="space-y-2">
      <div className="flex justify-between items-center">
        <Label>{label}</Label>
        {(resolutionInfo || sizeInfo) && (
          <span className="text-[10px] font-semibold text-primary uppercase">
            {resolutionInfo} {sizeInfo && `| ${sizeInfo}`}
          </span>
        )}
      </div>
      <div className="flex items-center gap-4">
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-dashed border-input bg-muted hover:border-muted-foreground/50"
        >
          {preview ? (
            <img src={preview} alt="" className="h-full w-full object-cover" />
          ) : (
            <ImageIcon className="h-6 w-6 text-muted-foreground" />
          )}
        </button>
        <div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={isUploading}
            onClick={() => fileInputRef.current?.click()}
          >
            {isUploading ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Upload className="mr-2 h-4 w-4" />
            )}
            {isUploading ? 'Uploading...' : `Upload ${label.toLowerCase()}`}
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) onFileSelect(file);
            }}
          />
          {error && <p className="mt-1 text-sm text-destructive">{error}</p>}
        </div>
      </div>
    </div>
  );
}

interface ImageItem {
  id: string;
  preview: string;
  filename?: string;
  uploading: boolean;
}

interface MultiImageUploadFieldProps {
  label: string;
  items: ImageItem[];
  error?: string;
  resolutionInfo?: string;
  sizeInfo?: string;
  onFilesSelect: (files: FileList) => void;
  onRemove: (id: string) => void;
}

function MultiImageUploadField({
  label,
  items,
  error,
  resolutionInfo,
  sizeInfo,
  onFilesSelect,
  onRemove,
}: MultiImageUploadFieldProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  return (
    <div className="space-y-2">
      <div className="flex justify-between items-center">
        <Label>{label}</Label>
        {(resolutionInfo || sizeInfo) && (
          <span className="text-[10px] font-semibold text-primary uppercase">
            {resolutionInfo} {sizeInfo && `| ${sizeInfo}`}
          </span>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-3">
        {items.map((item) => (
          <div
            key={item.id}
            className="relative flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-input bg-muted"
          >
            {item.uploading ? (
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            ) : (
              <img src={item.preview} alt="" className="h-full w-full object-cover" />
            )}
            {!item.uploading && (
              <button
                type="button"
                onClick={() => onRemove(item.id)}
                className="absolute right-1 top-1 rounded-full bg-black/60 p-0.5 text-white hover:bg-black/80"
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </div>
        ))}
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="flex h-20 w-20 shrink-0 items-center justify-center rounded-lg border border-dashed border-input bg-muted hover:border-muted-foreground/50"
        >
          <ImageIcon className="h-6 w-6 text-muted-foreground" />
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => {
            if (e.target.files?.length) {
              onFilesSelect(e.target.files);
              e.target.value = '';
            }
          }}
        />
      </div>
      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  );
}

export function VendorFormDialog({ open, onOpenChange, vendor }: VendorFormDialogProps) {
  const isEditing = Boolean(vendor);
  const { success, error: showError } = useNotifications();
  const { data: categories } = useMerchantCategories();
  const { mutate: createMerchant, isPending: isCreating } = useCreateMerchant();
  const { mutate: updateMerchant, isPending: isUpdating } = useUpdateMerchant();
  const { mutate: createCategory, isPending: isAddingCategory } = useCreateMerchantCategory();
  const { mutate: uploadLogo, isPending: isUploadingLogo } = useUpload();
  const { mutate: uploadBanner, isPending: isUploadingBanner } = useUpload();
  const uploadErrorMessage = (err: AxiosError<{ message?: string }>, fallback: string) =>
    err.response?.data?.message || fallback;
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [bannerPreview, setBannerPreview] = useState<string | null>(null);
  const [imageItems, setImageItems] = useState<ImageItem[]>([]);
  const [stateSearch, setStateSearch] = useState('');
  const [selectedStateCode, setSelectedStateCode] = useState('');
  const [citySearch, setCitySearch] = useState('');
  const [addCategoryOpen, setAddCategoryOpen] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');

  const debouncedStateSearch = useDebouncedValue(stateSearch);
  const debouncedCitySearch = useDebouncedValue(citySearch);
  const { data: states, isLoading: isLoadingStates } = useStates(debouncedStateSearch);
  const { data: cities, isLoading: isLoadingCities } = useCities(
    selectedStateCode,
    debouncedCitySearch
  );
  const stateOptions = (states ?? []).map((s) => ({ value: s.code, label: s.name }));
  const cityOptions = (cities ?? []).map((c) => ({ value: c.name, label: c.name }));

  const {
    register,
    handleSubmit,
    control,
    reset,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<VendorFormData>({
    resolver: zodResolver(vendorSchema),
    defaultValues: emptyDefaults,
  });

  useEffect(() => {
    if (!open) return;

    if (vendor) {
      reset(merchantToFormData(vendor));
      setLogoPreview(vendor.logo || null);
      setBannerPreview(vendor.banner || null);
      setImageItems(
        (vendor.images ?? []).map((url) => ({
          id: url,
          preview: url,
          filename: url,
          uploading: false,
        }))
      );
      setCitySearch(vendor.city);
      setStateSearch(vendor.state ?? '');
      setSelectedStateCode('');
    } else {
      reset(emptyDefaults);
      setLogoPreview(null);
      setBannerPreview(null);
      setImageItems([]);
      setCitySearch('');
      setStateSearch('');
      setSelectedStateCode('');
    }
  }, [open, vendor, reset]);

  const handleSelectState = (option: { value: string; label: string }) => {
    setSelectedStateCode(option.value);
    setStateSearch(option.label);
    setValue('state', option.label, { shouldValidate: true });
    setCitySearch('');
    setValue('city', '');
  };

  const handleSelectCity = (option: { value: string; label: string }) => {
    setCitySearch(option.label);
    setValue('city', option.value, { shouldValidate: true });
  };

  const closeAddCategoryDialog = () => {
    setAddCategoryOpen(false);
    setNewCategoryName('');
    // Radix can leave document.body stuck with pointer-events: none after a
    // nested dialog closes, making the underlying form dialog unclickable.
    setTimeout(() => {
      document.body.style.pointerEvents = '';
    }, 0);
  };

  const handleAddCategory = () => {
    const name = newCategoryName.trim();
    if (!name) return;

    createCategory(
      { name },
      {
        onSuccess: (category) => {
          success('Category added');
          const currentIds = getValues('industryCategoryIds') || [];
          setValue('industryCategoryIds', [...currentIds, String(category.id)], {
            shouldValidate: true,
          });
          closeAddCategoryDialog();
        },
        onError: (err) => {
          showError(err.response?.data?.message || 'Failed to add category');
        },
      }
    );
  };

  const validateImage = (
    file: File,
    label: string,
    maxSizeMB: number,
    minWidth: number,
    minHeight: number
  ): Promise<boolean> => {
    return new Promise((resolve) => {
      // Size check
      const maxSizeBytes = maxSizeMB * 1024 * 1024;
      if (file.size > maxSizeBytes) {
        showError(`${label} file size must be less than ${maxSizeMB} MB.`);
        resolve(false);
        return;
      }
      if (file.size < 2 * 1024) {
        showError(`${label} file size must be at least 2 KB.`);
        resolve(false);
        return;
      }

      // Resolution check
      const img = new Image();
      img.onload = () => {
        URL.revokeObjectURL(img.src);
        if (img.naturalWidth < minWidth || img.naturalHeight < minHeight) {
          showError(`${label} resolution must be at least ${minWidth}x${minHeight} px.`);
          resolve(false);
        } else {
          resolve(true);
        }
      };
      img.onerror = () => {
        showError(`Invalid image file for ${label}.`);
        resolve(false);
      };
      img.src = URL.createObjectURL(file);
    });
  };

  const handleLogoSelect = async (file: File) => {
    const isValid = await validateImage(file, 'Logo', 1, 500, 500);
    if (!isValid) return;

    setLogoPreview(URL.createObjectURL(file));
    uploadLogo(file, {
      onSuccess: (result) => {
        setValue('logo', result.meta.filename, { shouldValidate: true });
        success('Logo uploaded');
      },
      onError: (err) => {
        showError(uploadErrorMessage(err, 'Logo upload failed'));
        setLogoPreview(null);
      },
    });
  };

  const handleBannerSelect = async (file: File) => {
    const isValid = await validateImage(file, 'Banner', 3, 1200, 400);
    if (!isValid) return;

    setBannerPreview(URL.createObjectURL(file));
    uploadBanner(file, {
      onSuccess: (result) => {
        setValue('banner', result.meta.filename, { shouldValidate: true });
        success('Banner uploaded');
      },
      onError: (err) => {
        showError(uploadErrorMessage(err, 'Banner upload failed'));
        setBannerPreview(null);
      },
    });
  };

  const syncImagesValue = (items: ImageItem[]) => {
    setValue(
      'images',
      items.filter((item) => item.filename).map((item) => item.filename as string),
      { shouldValidate: true }
    );
  };

  const handleImagesSelect = async (files: FileList) => {
    const fileArray = Array.from(files);
    for (const file of fileArray) {
      const isValid = await validateImage(file, 'Vendor Image', 2, 800, 600);
      if (!isValid) continue;

      const id = crypto.randomUUID();
      setImageItems((prev) => [
        ...prev,
        { id, preview: URL.createObjectURL(file), uploading: true },
      ]);
      uploadFile(file)
        .then((result) => {
          setImageItems((prev) => {
            const next = prev.map((item) =>
              item.id === id ? { ...item, filename: result.meta.filename, uploading: false } : item
            );
            syncImagesValue(next);
            return next;
          });
          success('Image uploaded');
        })
        .catch((err: AxiosError<{ message?: string }>) => {
          showError(uploadErrorMessage(err, 'Image upload failed'));
          setImageItems((prev) => prev.filter((item) => item.id !== id));
        });
    }
  };

  const handleRemoveImage = (id: string) => {
    setImageItems((prev) => {
      const next = prev.filter((item) => item.id !== id);
      syncImagesValue(next);
      return next;
    });
  };

  const onSubmit = (data: VendorFormData) => {
    const categoryIds = data.industryCategoryIds.map(Number);
    const primaryCategoryId = categoryIds[0] || 0;

    const { industryCategoryIds: _, ...rest } = data;

    const payload = {
      ...rest,
      industryCategoryId: primaryCategoryId,
      industryCategoryIds: categoryIds,
      address: data.address ?? '',
      mapUrl: data.mapUrl ?? '',
      websiteUrl: data.websiteUrl ?? '',
      businessEmail: data.businessEmail ?? '',
      contactMobile: `+91${data.contactMobile}`,
    };

    if (isEditing && vendor) {
      updateMerchant(
        { merchantId: vendor.id, payload },
        {
          onSuccess: () => {
            success('Merchant updated successfully');
            onOpenChange(false);
          },
          onError: (err) => {
            showError(err.response?.data?.message || 'Failed to update merchant');
          },
        }
      );
      return;
    }

    createMerchant(payload, {
      onSuccess: () => {
        success('Merchant created successfully');
        onOpenChange(false);
      },
      onError: (err) => {
        showError(err.response?.data?.message || 'Failed to create merchant');
      },
    });
  };

  const isSubmitting = isCreating || isUpdating;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl">
          <DialogHeader className="text-center sm:text-center">
            <DialogTitle className="text-center text-xl font-bold">{isEditing ? 'Edit Merchant' : 'Add Merchant'}</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <ImageUploadField
                label="Logo"
                preview={logoPreview}
                isUploading={isUploadingLogo}
                error={errors.logo?.message}
                resolutionInfo="MIN. 500X500 PX (1:1)"
                sizeInfo="2KB - 1MB"
                onFileSelect={handleLogoSelect}
              />
              <ImageUploadField
                label="Banner"
                preview={bannerPreview}
                isUploading={isUploadingBanner}
                error={errors.banner?.message}
                resolutionInfo="MIN. 1200X400 PX"
                sizeInfo="2KB - 3MB"
                onFileSelect={handleBannerSelect}
              />
            </div>

            <MultiImageUploadField
              label="Images"
              items={imageItems}
              error={errors.images?.message}
              resolutionInfo="MIN. 800X600 PX"
              sizeInfo="2KB - 2MB"
              onFilesSelect={handleImagesSelect}
              onRemove={handleRemoveImage}
            />

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  placeholder="e.g. Sharma General Store"
                  {...register('name')}
                  className={errors.name ? 'border-destructive' : ''}
                />
                {errors.name && <p className="text-sm text-destructive">{errors.name.message}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="industryCategoryIds">Category</Label>
                <Controller
                  name="industryCategoryIds"
                  control={control}
                  render={({ field }) => (
                    <MultiSelect
                      options={(categories ?? []).map((category) => ({
                        value: String(category.id),
                        label: category.name,
                      }))}
                      selectedValues={field.value}
                      onChange={field.onChange}
                      placeholder="Select categories"
                      onAddCategory={() => setAddCategoryOpen(true)}
                      error={Boolean(errors.industryCategoryIds)}
                    />
                  )}
                />
                {errors.industryCategoryIds && (
                  <p className="text-sm text-destructive">{errors.industryCategoryIds.message}</p>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                placeholder="Briefly describe what this vendor offers"
                {...register('description')}
                className={errors.description ? 'border-destructive' : ''}
              />
              {errors.description && (
                <p className="text-sm text-destructive">{errors.description.message}</p>
              )}
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>State</Label>
                <SearchableSelect
                  searchTerm={stateSearch}
                  onSearchTermChange={setStateSearch}
                  options={stateOptions}
                  onSelect={handleSelectState}
                  placeholder="Search state..."
                  isLoading={isLoadingStates}
                  error={Boolean(errors.state)}
                />
                {errors.state && <p className="text-sm text-destructive">{errors.state.message}</p>}
              </div>

              <div className="space-y-2">
                <Label>City</Label>
                <SearchableSelect
                  searchTerm={citySearch}
                  onSearchTermChange={setCitySearch}
                  options={cityOptions}
                  onSelect={handleSelectCity}
                  placeholder={selectedStateCode ? 'Search city...' : 'Select a state first'}
                  isLoading={isLoadingCities}
                  disabled={!selectedStateCode}
                  error={Boolean(errors.city)}
                />
                {errors.city && <p className="text-sm text-destructive">{errors.city.message}</p>}
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="address">Address</Label>
                <Input
                  id="address"
                  placeholder="e.g. 12 MG Road, Near City Mall"
                  {...register('address')}
                  className={errors.address ? 'border-destructive' : ''}
                />
                {errors.address && (
                  <p className="text-sm text-destructive">{errors.address.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="mapUrl">Map URL</Label>
                <Input
                  id="mapUrl"
                  placeholder="https://maps.google.com/..."
                  {...register('mapUrl')}
                  className={errors.mapUrl ? 'border-destructive' : ''}
                />
                {errors.mapUrl && (
                  <p className="text-sm text-destructive">{errors.mapUrl.message}</p>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="earnRate">Earn Rate</Label>
              <Input
                id="earnRate"
                type="number"
                step="any"
                placeholder="e.g. 5"
                {...register('earnRate')}
                className={errors.earnRate ? 'border-destructive' : ''}
              />
              {errors.earnRate && (
                <p className="text-sm text-destructive">{errors.earnRate.message}</p>
              )}
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="websiteUrl">Website URL (optional)</Label>
                <Input
                  id="websiteUrl"
                  placeholder="www.example.com"
                  {...register('websiteUrl')}
                  className={errors.websiteUrl ? 'border-destructive' : ''}
                />
                {errors.websiteUrl && (
                  <p className="text-sm text-destructive">{errors.websiteUrl.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="businessEmail">Business Email (optional)</Label>
                <Input
                  id="businessEmail"
                  type="email"
                  placeholder="vendor@example.com"
                  {...register('businessEmail')}
                  className={errors.businessEmail ? 'border-destructive' : ''}
                />
                {errors.businessEmail && (
                  <p className="text-sm text-destructive">{errors.businessEmail.message}</p>
                )}
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="contactPerson">Contact Person</Label>
                <Input
                  id="contactPerson"
                  placeholder="e.g. Ramesh Sharma"
                  {...register('contactPerson')}
                  className={errors.contactPerson ? 'border-destructive' : ''}
                />
                {errors.contactPerson && (
                  <p className="text-sm text-destructive">{errors.contactPerson.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="contactMobile">Contact Mobile</Label>
                <div
                  className={cn(
                    'flex rounded-md border border-input overflow-hidden focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2',
                    errors.contactMobile && 'border-destructive'
                  )}
                >
                  <span className="flex items-center bg-muted/60 px-3 text-sm font-medium text-muted-foreground border-r border-input shrink-0 select-none">
                    +91
                  </span>
                  <input
                    id="contactMobile"
                    type="text"
                    placeholder="Enter MobileNumber"
                    {...register('contactMobile')}
                    className="flex h-10 w-full bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                  />
                </div>
                {errors.contactMobile && (
                  <p className="text-sm text-destructive">{errors.contactMobile.message}</p>
                )}
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting || isUploadingLogo || isUploadingBanner}>
                {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {isEditing ? 'Save Changes' : 'Create Merchant'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog
        open={addCategoryOpen}
        onOpenChange={(state) => {
          if (state) {
            setAddCategoryOpen(true);
          } else {
            closeAddCategoryDialog();
          }
        }}
      >
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Add Category</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="newCategoryName">Category Name</Label>
            <Input
              id="newCategoryName"
              placeholder="e.g. Home Decor"
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddCategory();
                }
              }}
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={closeAddCategoryDialog}>
              Cancel
            </Button>
            <Button
              type="button"
              disabled={!newCategoryName.trim() || isAddingCategory}
              onClick={handleAddCategory}
            >
              {isAddingCategory && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Add
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
